/**
 * WebGL detection utilities to help identify problematic graphics configurations
 * Specifically targets detection of Chrome's ANGLE backend choice
 */
import { isChrome } from './browserDetection';
import { webglLogger } from './logger';

// Add global augmentation for typed getExtension
declare global {
  interface WebGLRenderingContext {
    getExtension(name: 'WEBGL_debug_renderer_info'): WEBGL_debug_renderer_info | null;
  }
}

/**
 * WebGL renderer information
 */
export interface WebGLInfo {
  version: string;
  renderer: string;
  vendor: string;
  shadingLanguageVersion: string;
  maxTextureSize: number;
  maxVertexUniformVectors: number;
  unmaskedVendor?: string;
  unmaskedRenderer?: string;
  usingOpenGL: boolean;
  usingAngle: boolean;
  isOptimal: boolean;
  webglVersion: number;
}

/**
 * Check if Chrome is using the OpenGL backend for ANGLE
 * This specific configuration causes performance issues with Three.js
 */
export function isChromeUsingOpenGL(): boolean {
  if (!isChrome) return false;
  
  const webglInfo = getWebGLInfo();
  return webglInfo.usingOpenGL;
}

/**
 * Safely get a WebGL parameter as a string.
 */
function getGLString(gl: WebGLRenderingContext, pname: number): string {
  const val: unknown = gl.getParameter(pname);
  return typeof val === 'string' ? val : '';
}

/**
 * Safely get a WebGL parameter as a number.
 */
function getGLNumber(gl: WebGLRenderingContext, pname: number): number {
  const val: unknown = gl.getParameter(pname);
  return typeof val === 'number' ? val : 0;
}

/**
 * Retrieves detailed WebGL context information.
 */
export const getWebGLInfo = (): WebGLInfo => {
  const isUsingOpenGL = isChrome && (typeof window !== 'undefined') && (navigator.userAgent.includes('OpenGL'));

  const defaultValue: WebGLInfo = {
    version: 'N/A',
    renderer: 'N/A',
    vendor: 'N/A',
    shadingLanguageVersion: 'N/A',
    maxTextureSize: 0,
    maxVertexUniformVectors: 0,
    unmaskedVendor: undefined,
    unmaskedRenderer: undefined,
    usingOpenGL: isUsingOpenGL,
    usingAngle: false,
    isOptimal: !isUsingOpenGL,
    webglVersion: 1,
  };

  if (typeof window === 'undefined') return defaultValue;

  try {
    const canvas = document.createElement('canvas');
    // Obtain contexts in a type-safe manner
    const ctx2: unknown = canvas.getContext('webgl2');
    const gl2 = ctx2 instanceof WebGL2RenderingContext ? ctx2 : null;
    const ctx1: unknown = canvas.getContext('webgl');
    const gl1 = ctx1 instanceof WebGLRenderingContext ? ctx1 : null;
    const ctxExp: unknown = canvas.getContext('experimental-webgl');
    const glExp = ctxExp instanceof WebGLRenderingContext ? ctxExp : null;
    const gl = gl2 ?? gl1 ?? glExp;

    if (gl) {
      // Typed extension retrieval
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const versionString = getGLString(gl, gl.VERSION) || 'N/A';
      const renderer = getGLString(gl, gl.RENDERER) || 'N/A';
      const vendor = getGLString(gl, gl.VENDOR) || 'N/A';
      const shadingLanguageVersion = getGLString(gl, gl.SHADING_LANGUAGE_VERSION) || 'N/A';
      const maxTextureSize = getGLNumber(gl, gl.MAX_TEXTURE_SIZE);
      const maxVertexUniformVectors = getGLNumber(gl, gl.MAX_VERTEX_UNIFORM_VECTORS);
      const unmaskedVendor = debugInfo ? getGLString(gl, debugInfo.UNMASKED_VENDOR_WEBGL) : undefined;
      const unmaskedRenderer = debugInfo ? getGLString(gl, debugInfo.UNMASKED_RENDERER_WEBGL) : undefined;

      const isWebGL2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
      const webglVersionNumber = isWebGL2 ? 2 : 1;

      const likelyUsingAngle = isChrome && (renderer.toLowerCase().includes('angle') || renderer.toLowerCase().includes('direct3d'));

      const isOptimalConfiguration = !(isChrome && isUsingOpenGL);

      return {
        version: versionString,
        renderer,
        vendor,
        shadingLanguageVersion,
        maxTextureSize,
        maxVertexUniformVectors,
        unmaskedVendor,
        unmaskedRenderer,
        usingOpenGL: isUsingOpenGL,
        usingAngle: likelyUsingAngle,
        isOptimal: isOptimalConfiguration,
        webglVersion: webglVersionNumber,
      };
    } else {
      webglLogger.warn('Could not get WebGL context.');
      return defaultValue;
    }
  } catch (e) {
    webglLogger.warn('Error retrieving WebGL info:', e);
    return defaultValue;
  }
};

/**
 * Get instructions for changing Chrome's ANGLE backend
 * Returns an object with instructions and a URL to chrome://flags
 */
export function getChromeGraphicsFixInstructions(): {
  title: string;
  description: string;
  instructions: string[];
  url: string;
  note: string;
} {
  return {
    title: "Graphics Performance Issue Detected",
    description: "Your Chrome browser is using OpenGL for WebGL content, which can cause performance issues with 3D graphics.",
    instructions: [
      "1. Copy and paste this in a new Chrome tab: chrome://flags/#use-angle",
      "2. Change the 'Choose ANGLE graphics backend' setting from 'OpenGL' to 'Default'",
      "3. Restart Chrome when prompted",
      "4. Refresh this page"
    ],
    url: "chrome://flags/#use-angle",
    note: "This only affects Chrome on Windows and won't impact other browsers."
  };
} 
